// LoginUserScreen.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom'; // useNavigate for redirection after login
import Logo from '../../components/Branding/Logo'; // Import the Logo component
import TextInput from '../../components/FormElements/TextInput'; // Import the refactored TextInput
import { useQuestionnaire } from '../../contexts/QuestionnaireContext';
import { useAuth } from '../../contexts/AuthContext'; // Import useAuth
import { useLocation } from 'react-router-dom'; // Import useLocation

function LoginUserScreen() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginError, setLoginError] = useState(null);
  const [loginAttempted, setLoginAttempted] = useState(false);
  const [initialAuthCheckDone, setInitialAuthCheckDone] = useState(false);
  const [initialNavDecisionMade, setInitialNavDecisionMade] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { getAllAnswers, loadAnswersFromJSON, clearAllAnswers } = useQuestionnaire();
  const { login, isAuthenticated, user, role: authRole, token: authToken, updateUserContext } = useAuth(); // Assuming updateUserContext exists

  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    console.log('[LoginUserScreen handleSubmit] Start. Current isAuthenticated (from useAuth):', isAuthenticated);

    setIsSubmitting(true);
    setLoginAttempted(false);
    setLoginError(null);
    console.log('[LoginUserScreen handleSubmit] User Login attempt with:', formData);

    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

    try {
      const loginResponse = await fetch(`${API_BASE_URL}/api/users/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const loginData = await loginResponse.json();

      if (!loginResponse.ok) {
        const errorMessage = loginData.error?.message || loginData.message || `Login failed: ${loginResponse.status}`;
        if (isMounted.current) setLoginError(errorMessage);
        console.error('[LoginUserScreen handleSubmit] Login API failed:', errorMessage);
        return;
      }

      console.log('[LoginUserScreen handleSubmit] Login API successful:', loginData);

      if (loginData.token && loginData.role) {
        console.log(`[LoginUserScreen handleSubmit] Role: ${loginData.role}. Fetching user details...`);
        try {
          const userDetailsResponse = await fetch(`${API_BASE_URL}/api/users/me`, {
            headers: { 'Authorization': `Bearer ${loginData.token}` },
          });
          const fetchedUserData = await userDetailsResponse.json();

          if (!userDetailsResponse.ok) {
            const userErrorMessage = fetchedUserData.error?.message || fetchedUserData.message || `Failed to fetch user: ${userDetailsResponse.status}`;
            if (isMounted.current) setLoginError(userErrorMessage);
            console.error('[LoginUserScreen handleSubmit] Failed to fetch user details:', userErrorMessage);
            return;
          }

          console.log('[LoginUserScreen handleSubmit] User details fetched:', fetchedUserData);
          const userObject = fetchedUserData.user || fetchedUserData;
          let imageUrl = null;
          if (userObject?.avatar_filepath) {
            const baseApiUrl = (process.env.REACT_APP_API_BASE_URL).replace(/\/$/, '');
            const imagePath = userObject.avatar_filepath.replace(/^\//, '');
            imageUrl = `${imagePath}`;
          }

          const authUserObject = {
            ...userObject,
            name: userObject.name || userObject.email,
            image: imageUrl,
          };

          login(loginData.token, loginData.role, userObject.id, authUserObject);
          console.log(`[LoginUserScreen handleSubmit] Context login() called.`);
          if (isMounted.current) {
            setLoginAttempted(true);
            console.log('[LoginUserScreen handleSubmit] Set loginAttempted to true.');
          }
        } catch (fetchUserError) {
          console.error('[LoginUserScreen handleSubmit] Error fetching user details:', fetchUserError.message);
          if (isMounted.current) setLoginError(fetchUserError.message || 'Failed to fetch user data.');
        }
      } else {
        if (isMounted.current) setLoginError('Token or role not received from server.');
        console.error('[LoginUserScreen handleSubmit] Token or role missing in login response.');
      }
    } catch (error) {
      console.error('[LoginUserScreen handleSubmit] Outer catch error:', error.message);
      if (isMounted.current && !loginError) {
        setLoginError(error.message || 'An unexpected login error occurred.');
      }
    } finally {
      if (isMounted.current) {
        setIsSubmitting(false);
        console.log('[LoginUserScreen handleSubmit] Finally: isSubmitting set to false.');
      }
    }
  };

  const performQuestionnaireNavigation = async () => {
    if (!isMounted.current) {
      console.log('[PQN] Unmounted during navigation logic.');
      return;
    }
    console.log('[PQN] Starting questionnaire navigation logic...');

    // Check if there's a preserved location from ProtectedRoute redirect
    const from = location.state?.from?.pathname || location.state?.from;
    if (from && from !== '/login') {
      console.log('[PQN] Found preserved location from ProtectedRoute:', from);
      navigate(from, { replace: true });
      return;
    }

    let currentUserData = user; // Start with user data from context

    // If user is authenticated, re-fetch their latest data to ensure initial_form_json is fresh
    // This is particularly important if they are sent here after an action elsewhere (like modal from step1_6)
    // that might have updated their backend data.
    if (isAuthenticated && authToken) {
      console.log('[PQN] Authenticated user. Re-fetching latest user data to ensure freshness of initial_form_json.');
      const API_BASE_URL_PQN = process.env.REACT_APP_API_BASE_URL;
      try {
        const response = await fetch(`${API_BASE_URL_PQN}/api/users/me`, {
          headers: { 'Authorization': `Bearer ${authToken}` },
        });
        if (!isMounted.current) return;

        if (response.ok) {
          const data = await response.json();
          currentUserData = data.user || data; // Use fresh data
          console.log('[PQN] Fetched fresh user data:', currentUserData);
          // Optionally, update AuthContext if you have a direct way and it's deemed safe
          if (updateUserContext) { // Check if updateUserContext exists
            updateUserContext(currentUserData); // Update context with fresh data
            console.log('[PQN] AuthContext updated with fresh user data.');
          }
        } else {
          console.warn('[PQN] Failed to fetch fresh user data, proceeding with potentially stale context user data. Status:', response.status);
          // Fallback to user from context if fetch fails
        }
      } catch (error) {
        if (!isMounted.current) return;
        console.warn('[PQN] Error fetching fresh user data, proceeding with potentially stale context user data:', error);
        // Fallback to user from context on error
      }
    }


    if (currentUserData && currentUserData.hasOwnProperty('initial_form_json')) {
      const currentInitialFormJson = currentUserData.initial_form_json;
      console.log('[PQN] Using initial_form_json:', currentInitialFormJson);

      if (currentInitialFormJson === null || (typeof currentInitialFormJson === 'object' && Object.keys(currentInitialFormJson).length === 0)) {
        console.log('[PQN] initial_form_json is null or empty. Checking context answers.');
        const allAnswers = await getAllAnswers(); // These are answers from QuestionnaireContext

        if (allAnswers && Object.keys(allAnswers).length > 0) {
          console.log('[PQN] Answers found in Context:', allAnswers);
          const requiredSteps = ['step1_1', 'step1_2', 'step1_3', 'step1_4', 'step1_5', 'step1_6'];
          const isCompleteBasic = requiredSteps.every(step => allAnswers.hasOwnProperty(step) && allAnswers[step] !== null && allAnswers[step] !== undefined);

          if (isCompleteBasic) {
            console.log('[PQN] Basic steps (1-1 to 1-6) complete in context. Attempting to save these as initial_form_json.');
            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
            try {
              const updateResponse = await fetch(`${API_BASE_URL}/api/users/${currentUserData.id}`, { // Use currentUserData.id
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
                body: JSON.stringify({ initial_form_json: allAnswers }),
              });
              if (!isMounted.current) return;
              if (updateResponse.ok) {
                console.log('[PQN] initial_form_json updated on backend with context answers. Navigating to questions/step2_1');
                // The user object in AuthContext should be updated by a mechanism after this PUT.
                // For now, we assume DynamicQuestionDriver will load based on the now-updated backend.
                // No clearAllAnswers() here.
                navigate('/questions/step2_1');
              } else {
                const updateErrorData = await updateResponse.json();
                const errMsg = updateErrorData.error?.message || updateErrorData.message || 'Failed to update initial_form_json';
                console.error('[PQN] Error updating initial_form_json:', errMsg);
                alert("Hubo un problema guardando tu progreso inicial. Por favor, intenta de nuevo o continúa más tarde.");
              }
            } catch (putError) {
              if (!isMounted.current) return;
              console.error('[PQN] PUT error for initial_form_json:', putError);
              alert("Error técnico al guardar progreso. Por favor, intenta de nuevo.");
            }
          } else {
            console.log('[PQN] Not all required steps (1-1 to 1-6) complete in context. Navigating to questions/step1_1.');
            navigate('/questions/step1_1');
          }
        } else {
          console.log('[PQN] No answers in QuestionnaireContext (and initial_form_json was null/empty). Navigating to questions/step1_1.');
          navigate('/questions/step1_1');
        }
      } else { // initial_form_json is populated
        console.log('[PQN] initial_form_json is populated. Loading these answers into context.');

        // Check for full completion directly using currentInitialFormJson
        const finalStep = 'step5_4';
        const isCompleteFull = currentInitialFormJson.hasOwnProperty(finalStep) &&
          currentInitialFormJson[finalStep] !== null &&
          currentInitialFormJson[finalStep] !== undefined;

        if (isCompleteFull) {
          console.log('[PQN] Questionnaire fully completed (based on initial_form_json). Navigating to /home.');
          await loadAnswersFromJSON(currentInitialFormJson); // Load answers for consistency if navigating to /home
          navigate('/home');
        } else {
          console.log('[PQN] Questionnaire partially completed (based on initial_form_json). Navigating to questions/step2_1.');
          await loadAnswersFromJSON(currentInitialFormJson); // Load answers before navigating to continue
          navigate('/questions/step2_1');
        }
      }
    } else {
      console.warn('[PQN] User data or initial_form_json property missing after potential fetch. Defaulting to /home.');
      navigate('/home');
    }
  };

  // Effect 1: Mark initial auth check as done once component mounts.
  useEffect(() => {
    if (isMounted.current && !initialAuthCheckDone) {
      console.log('[LoginUserScreen Effect 1] Component mounted. Setting initialAuthCheckDone.');
      setInitialAuthCheckDone(true);
    }
  }, [initialAuthCheckDone]);

  // Simplified auth state handler
  useEffect(() => {
    if (!isMounted.current) return;

    if (isAuthenticated) {
      console.log(`[LoginUserScreen] Authenticated as ${authRole}, handling navigation`);

      // Check if there's a preserved location from ProtectedRoute redirect
      const from = location.state?.from?.pathname || location.state?.from;
      if (from && from !== '/login') {
        console.log('[LoginUserScreen] Found preserved location from ProtectedRoute:', from);
        navigate(from, { replace: true });
        return;
      }

      if (authRole === 'user') {
        performQuestionnaireNavigation();
      } else if (authRole === 'admin') {
        navigate('/admin/dashboard');
      }
    }
  }, [isAuthenticated, authRole, navigate, location.state]);

  // Handle successful login attempts
  useEffect(() => {
    if (!isMounted.current || !loginAttempted) return;

    if (isAuthenticated) {
      console.log(`[LoginUserScreen] Login successful for ${authRole}`);

      // Check if there's a preserved location from ProtectedRoute redirect
      const from = location.state?.from?.pathname || location.state?.from;
      if (from && from !== '/login') {
        console.log('[LoginUserScreen] Found preserved location from ProtectedRoute:', from);
        navigate(from, { replace: true });
        setLoginAttempted(false);
        return;
      }

      if (authRole === 'user') {
        performQuestionnaireNavigation();
      } else if (authRole === 'admin') {
        navigate('/admin/dashboard');
      }
    }
    setLoginAttempted(false);
  }, [loginAttempted, isAuthenticated, authRole, navigate, location.state]);


  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="col-md-6 form-side">
      <div className="form-container text-center">
        <Logo />
        <h1 className="text-success fw-bold mb-4">¡Bienvenido viajero!</h1>
        <h5 className="mb-5">A planificar tu viaje soñado a través de Checkin</h5>
        {loginError && <div className="alert alert-danger" role="alert">{loginError}</div>}
        <form onSubmit={handleSubmit} noValidate>
          <TextInput
            type="email"
            id="email"
            name="email"
            placeholder="Email"
            ariaLabel="Email"
            value={formData.email}
            onChange={handleChange}
            required
            disabled={isSubmitting}
            iconClass="bi bi-envelope"
            wrapperClassName="mb-3"
          />
          <TextInput
            type={showPassword ? "text" : "password"}
            id="password"
            name="password"
            placeholder="Contraseña"
            ariaLabel="Contraseña"
            value={formData.password}
            onChange={handleChange}
            required
            disabled={isSubmitting}
            iconClass={`bi ${showPassword ? 'bi-eye' : 'bi-eye-slash'}`}
            onIconClick={togglePasswordVisibility}
            iconAriaLabel="Mostrar u ocultar contraseña"
            wrapperClassName="mb-0"
          />
          <div className="text-end mt-2"><Link to="/forgot-password" className="small">¿Olvidaste tu contraseña?</Link></div>

          <div className="buttons-container d-grid gap-3 mt-5 mb-5">
            <button type="submit" className="btn btn-checkin" disabled={isSubmitting}>
              {isSubmitting ? 'Iniciando...' : 'Iniciar sesión'}
            </button>
            <Link to="/register" className={`btn btn-outline-checkin ${isSubmitting ? 'disabled' : ''}`} role="button" aria-disabled={isSubmitting}>Registrarme</Link>
          </div>
        </form>
        <p className="mt-3 text-muted small">protegido por reCAPTCHA · <a href="#">Privacidad</a> · <a href="#">Condiciones</a></p>
      </div>
    </div>
  );
}

export default LoginUserScreen;
